// Dia útil BANCÁRIO brasileiro — porta de finapp/lib/businessDay.ts (ADR 0082).
// Serve ao "Vencimento em dias úteis" do cartão: quando a data nominal cai em sábado,
// domingo ou feriado nacional, a fatura pode ser paga sem encargos no próximo dia útil
// (Lei 7.089/83). A data derivada NUNCA é persistida — `invoices.dueDate` guarda sempre a
// nominal, e a útil sai daqui na leitura. Uso EXCLUSIVO nas superfícies de pagamento
// (exibição, "vence em X dias", virada de atrasada); competência e agrupamento por mês
// seguem a nominal.
//
// Feriados calculados localmente (sem API): fixos + móveis derivados da Páscoa. Só os
// nacionais — a compensação bancária não fecha em feriado estadual ou municipal.

/** Domingo de Páscoa do ano (computus gregoriano anônimo). */
function easterSunday(year: number): { month: number; day: number } {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  return { month: Math.floor((h + l - 7 * m + 114) / 31), day: ((h + l - 7 * m + 114) % 31) + 1 };
}

const FIXED_HOLIDAYS_MMDD = [
  '01-01', // Confraternização Universal
  '04-21', // Tiradentes
  '05-01', // Dia do Trabalho
  '09-07', // Independência
  '10-12', // Nossa Senhora Aparecida
  '11-02', // Finados
  '11-15', // Proclamação da República
  '12-25', // Natal
  '12-31', // Último dia do ano: banco fecha (feriado bancário, como o Carnaval)
];

const pad = (n: number) => String(n).padStart(2, '0');
const isoOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
};
const shift = (iso: string, days: number) => {
  const d = parseISO(iso);
  d.setDate(d.getDate() + days);
  return isoOf(d);
};

const holidayCache = new Map<number, Set<string>>();

/** Feriados bancários nacionais do ano, como Set de 'YYYY-MM-DD'. */
export function nationalBankHolidays(year: number): Set<string> {
  const cached = holidayCache.get(year);
  if (cached) return cached;
  const holidays = new Set(FIXED_HOLIDAYS_MMDD.map(mmdd => `${year}-${mmdd}`));
  // Consciência Negra só virou feriado nacional em 2024 (Lei 14.759/2023): uma fatura
  // de 20/11/2023 caiu em dia útil normal e não pode rolar retroativamente.
  if (year >= 2024) holidays.add(`${year}-11-20`);
  const e = easterSunday(year);
  const easter = `${year}-${pad(e.month)}-${pad(e.day)}`;
  holidays.add(shift(easter, -48)); // Segunda de Carnaval
  holidays.add(shift(easter, -47)); // Terça de Carnaval
  holidays.add(shift(easter, -2)); // Sexta-feira Santa
  holidays.add(shift(easter, 60)); // Corpus Christi
  holidayCache.set(year, holidays);
  return holidays;
}

/** Seg–sex fora dos feriados nacionais. Data malformada conta como útil (não estoura no render). */
export function isBankBusinessDay(iso: string): boolean {
  const date = parseISO(iso);
  if (Number.isNaN(date.getTime())) return true;
  const weekday = date.getDay();
  if (weekday === 0 || weekday === 6) return false;
  return !nationalBankHolidays(date.getFullYear()).has(iso);
}

/** O próprio dia se já for útil; senão o próximo — sempre pra frente, nunca antecipa. */
export function nextBankBusinessDay(iso: string): string {
  let result = iso;
  for (let i = 0; i < 14 && !isBankBusinessDay(result); i += 1) result = shift(result, 1);
  return result;
}

/** Vencimento útil da fatura: a nominal rolada quando o cartão tem a opção ligada. */
export function effectiveDueDate(dueDate: string, dueOnBusinessDay: number | boolean | null | undefined): string {
  if (!dueOnBusinessDay) return dueDate;
  return nextBankBusinessDay(dueDate);
}
